import { withStyles } from "@material-ui/styles";
import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { isBrowser } from "react-device-detect";
import { compose } from "recompose";
import { Chip, Grid, Radio, TextField, Typography } from "@material-ui/core";
import ApplicationUtil from "../../../../../../../utils/ApplicationUtil";
import { CustomTextField } from "../../../../../../common/CustomTextField";
import Snackbar from "../../../../../../common/Snackbar";
import { Clear, Remove } from "@material-ui/icons";
import SubmitCancelBtn from "../../../../../../common/SubmitCancelBtn";
import Properties from "../../../../../../../properties/Properties";
import {
  createQuizInChallenge,
  getListQuizQuestion,
  getQuizQuestion,
} from "../../../../../../../apis/eventService";
import QuizQuestionTable from "./QuizQuestionTable";

const QuizCreationComponent = ({ classes, isPastChallenge }) => {
  const currentChallenge = ApplicationUtil?.getProgramUnderCurrentChallenge();

  const [quizState, setQuizState] = useState({
    quizText: "",
    options: [],
    optionInput: "",
    inputView: false,
    quizFieldError: false,
  });
  const [quizQuestions, setQuizQuestions] = useState([]);

  const { quizText, options, optionInput, inputView, quizFieldError } =
    quizState;

  const fetchQuizData = () => {
    if (!currentChallenge?.id) return;
    const params = {
      challengeType: currentChallenge?.programChallengeType || "",
    };

    getQuizQuestion(params)
      .then((res) => {
        if (res.status === 200) {
          setQuizQuestions(res?.data || []);
        }
      })
      .catch((err) => {
        console.error("Error fetching quiz:", err);
      });
  };

  useEffect(() => {
    fetchQuizData();
  }, [currentChallenge]);

  const onAddOption = () => {
    setQuizState((prev) => ({ ...prev, inputView: true }));
  };

  const saveOption = () => {
    if (optionInput.trim()) {
      const isDuplicate = options.some((opt) => opt.option === optionInput);
      if (isDuplicate) {
        Snackbar.show({
          variant: "error",
          message: "This option already exists.",
        });
      } else {
        setQuizState((prev) => ({
          ...prev,
          options: [...prev.options, { option: optionInput, isCorrect: false }],
          inputView: false,
          optionInput: "",
        }));
      }
    } else {
      Snackbar.show({ variant: "error", message: "Please enter option text." });
    }
  };

  const handleDeleteOption = (optionValue) => {
    setQuizState((prev) => ({
      ...prev,
      options: prev.options.filter((opt) => opt.option !== optionValue),
    }));
  };

  const handleCorrectAnswerChange = (optionValue) => {
    const updatedOptions = options.map((opt) => ({
      ...opt,
      isCorrect: opt.option === optionValue, // Only one can be true
    }));
    setQuizState((prev) => ({ ...prev, options: updatedOptions }));
  };

  const submitQuiz = () => {
    const paramsData = {
      quiz: quizText,
      programChallengeType: currentChallenge?.programChallengeType || "",
      options: options,
    };

    createQuizInChallenge(paramsData).then((res) => {
      if (res?.status === 200 && res?.data?.code !== "400") {
        Snackbar.show({
          variant: "success",
          message: "Quiz created successfully",
        });
        clearQuiz();
        fetchQuizData();
      } else {
        Snackbar.show({ variant: "error", message: "Failed to create quiz" });
      }
    });
  };

  const clearQuiz = () => {
    setQuizState({
      quizText: "",
      options: [],
      optionInput: "",
      inputView: false,
      quizFieldError: false,
    });
  };

  const validateAndSave = () => {
    if (!quizText.trim()) {
      setQuizState((prev) => ({ ...prev, quizFieldError: true }));
      Snackbar.show({
        variant: "error",
        message: "Please enter the quiz question.",
      });
      return;
    }
    if (options.length < 2) {
      Snackbar.show({
        variant: "error",
        message: "Please add at least two options.",
      });
      return;
    }
    if (!options.some((opt) => opt.isCorrect)) {
      Snackbar.show({
        variant: "error",
        message: "Please select one correct answer.",
      });
      return;
    }
    submitQuiz();
  };

  return (
    <div>
      <h3 className={classes.title}>{Properties.quizCreation_title}</h3>
      <Grid container spacing={1} style={{ padding: "10px 5px" }}>
        {/* Quiz Question Input */}
        <Grid
          item
          xs={12}
          style={{ display: "flex", padding: "6px 0", alignItems: "center" }}
        >
          <Typography style={{ width: "15%" }}>
            <span className={classes.labelCss}>
              {Properties.question_label}
              <span className={classes.mandatoryCss}>*</span> : &nbsp;
            </span>
          </Typography>
          <CustomTextField
            className={
              quizFieldError ? classes.fieldErrorCss : classes.fieldCss
            }
            placeholder={Properties.quizQuestion_placeholder}
            value={quizText}
            handleChange={() => (e) =>
              setQuizState((prev) => ({
                ...prev,
                quizText: e.target.value,
                quizFieldError: false,
              }))
            }
            readOnly={isPastChallenge}
          />
        </Grid>

        {/* Options Management */}
        <Grid item xs={12} style={{ padding: "6px 0", display: "flex" }}>
          <Typography style={{ width: "15%" }}>
            <span className={classes.labelCss}>
              {Properties.option_label}
              <span className={classes.mandatoryCss}>*</span> : &nbsp;
            </span>
          </Typography>
          <div
            className={classes.optionsCss}
            style={{ flexWrap: "wrap", width: "85%" }}
          >
            {options.map((item, index) => (
              <div key={index} style={{ padding: "5px" }}>
                <Chip
                  label={item.option}
                  onDelete={() => handleDeleteOption(item.option)}
                  deleteIcon={<Clear />}
                />
              </div>
            ))}
            {inputView ? (
              <>
                <TextField
                  value={optionInput}
                  onChange={(e) =>
                    setQuizState((prev) => ({
                      ...prev,
                      optionInput: e.target.value,
                    }))
                  }
                  placeholder={Properties.option_placeholder}
                />
                <SubmitCancelBtn
                  onClick={saveOption}
                  label={Properties.add_label}
                  style={{ width: "121px", marginLeft: "8px" }}
                  type="submit"
                />
              </>
            ) : (
              <SubmitCancelBtn
                onClick={onAddOption}
                label={Properties.addOption_label}
                style={{ width: "121px" }}
                type="submit"
              />
            )}
          </div>
        </Grid>

        {/* Expected Answer Selection (Radio style) */}
        <Grid item xs={12} style={{ padding: "6px 0", display: "flex" }}>
          <Typography style={{ width: "15%" }}>
            <span className={classes.expectedAnwerLabel}>
              {Properties.correct_answer_label}
              <span className={classes.mandatoryCss}>*</span> :
            </span>
          </Typography>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {options.length > 0 ? (
              options.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: "15px",
                  }}
                >
                  <Radio
                    checked={item.isCorrect}
                    onChange={() => handleCorrectAnswerChange(item.option)}
                    value={item.option}
                    color="primary"
                  />
                  <Typography>{item.option}</Typography>
                </div>
              ))
            ) : (
              <Remove style={{ color: "#727272" }} />
            )}
          </div>
        </Grid>
      </Grid>

      {!isPastChallenge && (
        <Grid container style={{ padding: "30px 0" }} spacing={2}>
          <Grid item xs={12}>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "20px" }}
            >
              <SubmitCancelBtn
                onClick={validateAndSave}
                label={Properties.create_quiz_label}
                style={{ width: "121px" }}
                type="submit"
              />
              <SubmitCancelBtn
                onClick={clearQuiz}
                label={Properties.clear_label}
                style={{ width: "121px" }}
                type="submit"
              />
            </div>
          </Grid>
        </Grid>
      )}

      <QuizQuestionTable
        list={quizQuestions?.result}
        fetchQuizData={fetchQuizData}
      />
    </div>
  );
};

const styles = (theme) => ({
  optionsCss: {
    display: "flex",
    flex: "flex-wrap",
  },
  expectedAnwerLabel: {
    color: "#727272",
    width: "fit-Content",
  },
  labelCss: {
    color: "#727272",
  },
  groupLabelCss: {
    fontSize: "20px",
    color: "rgb(8, 69, 108)",
  },
  mandatoryCss: { color: "red" },
  fieldCss: {
    border: "1px solid #08456C",
    height: "40px", //"45%",
    maxHeight: "40px",
    minHeight: "40px",
    width: isBrowser ? "360px" : "160px",
  },
  fieldErrorCss: {
    border: "1px solid red",
    height: "40px",
    maxHeight: "40px",
    minHeight: "40px",
    width: isBrowser ? "360px" : "160px",
  },
  title: {
    color: "rgb(8, 69, 108)",
  },

  option: {
    border: "1px solid #727272",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "10px",
    paddingBottom: "10px",
    // paddingRight: "",
  },
});

export default compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
)(QuizCreationComponent);
